import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { fetchFullCandidateReport } from '../../services/user/api';
import '../../styles/user/finalreport.css';
import logo from '../../assets/images/Logo.png';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js';
import jsPDF from 'jspdf';

const FinalReportPage = ({ candidateId, onBack }) => {
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchFullCandidateReport(candidateId)
      .then((res) => {
        if (res.data.status) {
          setReportData(res.data.data);
          //console.log("report:"+JSON.stringify(reportData))
        }
      })
      .catch((err) => {
        console.error('Error fetching report:', err);
      });
  }, [candidateId]);

  if (!reportData) return   <div className="centered-message">
            <div className="spinner"></div> 
            <div>Loading report...</div>
        </div> 

  const { employeeVerificationInfo, adminVerificationResult } = reportData;
  const {
    personalCheck,
    addressCheck,
    educationCheck,
    employmentCheck,
    employmentUANCheckRequests,
    criminalCheckRequests,
    attachmentResponses
  } = employeeVerificationInfo;

// const handleDownload = () => {
//   const element = document.getElementById('final-report-container');
//   const opt = {
//     margin:       0.2,
//     filename:     `Candidate_Report_${candidateId}.pdf`,
//     image:        { type: 'jpeg', quality: 1 },
//     html2canvas:  { scale: 2, useCORS: true },
//     jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
//   };

//   html2pdf().set(opt).from(element).save();
// };

// Add this line below your other imports (or convert logo to base64)
// const logoBase64 = 'YOUR_BASE64_STRING_HERE'; // Replace with the actual Base64 string for your logo



const handleDownload = async () => {
  const reportElement = document.getElementById('final-report-container');
  reportElement.classList.add('pdf-export-mode');

  //  Utility: Convert image URL to Base64 safely
  const loadImageAsBase64 = async (url) => {
  try {
    if (!url) return null;

    // Fetch the image as a blob
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) {
      console.warn("Failed to fetch image:", url, response.status);
      return null;
    }

    const blob = await response.blob();

    // Convert blob to Base64
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Error converting image to base64:", url, error);
    return null;
  }
};


  //  Convert logo to base64
  const base64Logo = await loadImageAsBase64(logo);

  // Convert all images inside report to base64
  const images = Array.from(reportElement.querySelectorAll('img'));
  await Promise.all(
    images.map((img) =>
      loadImageAsBase64(img.src).then((base64) => {
        if (base64) img.src = base64;
      })
    )
  );

  // Wait for DOM update
  await new Promise((r) => setTimeout(r, 300));

  //  Create html2pdf instance
  const opt = {
    margin: [10, 10, 25, 10], // extra bottom margin for footer
    filename: `Candidate_Report_${candidateId}.pdf`,
    image: { type: 'jpeg', quality: 1 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
  };

  //  Generate pdf using html2pdf, but before saving — add footer
  const worker = html2pdf().set(opt).from(reportElement);

  // Generate the pdf blob
  const pdf = await worker.toPdf().get('pdf');

  const pageCount = pdf.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Footer line (optional)
    // pdf.setDrawColor(200);
    // pdf.setLineWidth(0.3);
    // pdf.line(10, pageHeight - 15, pageWidth - 10, pageHeight - 15);

    // Page number (left)
    // pdf.setFontSize(10);
    // pdf.setTextColor(80);
    // pdf.text(`Page ${i} of ${pageCount}`, 10, pageHeight - 8);

    // Logo on the right
    if (base64Logo) {
      const logoWidth = 20;
      const logoHeight = 10;
      pdf.addImage(base64Logo, 'PNG', pageWidth - logoWidth - 10, pageHeight - logoHeight - 12, logoWidth, logoHeight);
    }
  }

  pdf.save(`Candidate_Report_${candidateId}.pdf`);

  reportElement.classList.remove('pdf-export-mode');
};




// helper to style verified text
const renderStatusCell = (value) => {
  const isVerified = typeof value === "string"
    ? value.toLowerCase() === "verified"
    : value === true;

  return (
    <span
      className={isVerified ? "report-status-verified" : "report-status-not-verified"}
    >
      {isVerified ? "Verified" : value || "Not Verified"}
    </span>
  );
};



  const severityLegend = [
    { type: 'Clear', color: '#388E3C', description: 'No significant disparity between stated and verified antecedents.' },
    { type: 'Minor Discrepant', color: '#FBC02D', description: 'Verification reports a mismatch of a lesser degree or lower impact.' },
    { type: 'Insufficient', color: '#7B1FA2', description: 'Verification could not be completed due to missing mandatory data or documents.' },
    { type: 'Attention Required', color: '#1976D2', description: 'Source is unable or refuses to share a complete response due to lack of data.' },
    { type: 'Discrepant', color: '#D32F2F', description: 'Verification reports a major mismatch with stated antecedents or the source is fake.' },
    { type: 'No Response', color: '#616161', description: 'No response received from the verification source by the report generation date.' },
  ];

const getStatusTextColor = (status) => {
  if (!status) return '#000';
  const match = severityLegend.find(
    (item) => item.type.toLowerCase() === status.toLowerCase()
  );
  return match ? match.color : '#000';
};



  const getStarsForStatus = (status) => {
  switch (status?.toLowerCase()) {
    case 'clear':
      return 5;
    case 'discrepant':
      return 1;
    case 'minor discrepant':
      return 4;
    case 'attention required':
      return 2;
    case 'insufficient':
      return 3;
    case 'no response':
      return 0;
    default:
      return 0;
  }
};


const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (section) {
    const yOffset = -80; 
    const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};



  return (
    <div className="final-container">
    <div className="final-report-container" id="final-report-container">
      <div className="report-header-section">
  <div className="report-header-left">
    <img src={logo} alt="Company Logo" className="report-logo" />
  </div>
      {/* <h1 className="report-title">Candidate Verification Report</h1> */}

  <div className="report-actions">
   
    <button onClick={handleDownload} className="report-action-button">
      <Download size={18} /> Download
    </button>
  </div>
</div>
 <div className='name-header'>
      <h3>BGC Report for {personalCheck?.firstName} {personalCheck?.lastName}</h3>
    </div>

<div className="report-table-container first-table">
  <table className="report-table">
    <thead>
      <tr>
        <th>ID</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Report Date</th>
        <th>Client</th>
        <th>Report Status</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>{personalCheck?.candidateId}</td>
        <td>{personalCheck?.firstName}</td>
        <td>{personalCheck?.lastName}</td>
        <td>{adminVerificationResult?.reportDate}</td>
        <td>Client Server Technology Solutions pvt ltd</td>
      <td style={{ color: getStatusTextColor(adminVerificationResult?.verificationStatus), fontWeight: 'bold' }}>
  {adminVerificationResult?.verificationStatus || 'N/A'}
          <br></br>
          <span className="status-stars" style={{ marginLeft: '5px' }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <span
                key={index}
                className={`star ${index < getStarsForStatus(adminVerificationResult?.verificationStatus) ? 'filled' : ''}`}
              >
                ★
              </span>
            ))}
          </span>
        </td>
      </tr>
    </tbody>
  </table>
</div>




      <div className="report-details-section">
        <div className="details-card">
          <h3 className="details-card-title">Candidate Details:</h3>
          <div className="details-row">
            <span className="details-label">Name : </span>
            <span className="details-value">{personalCheck?.firstName} {personalCheck?.lastName}</span>
          </div>
          <div className="details-row">
            <span className="details-label">Email : </span>
            <span className="details-value">{personalCheck?.email}</span>
          </div>
        </div>
        <div className="details-card">
          <h3 className="details-card-title">Verifier Details : </h3>
          <div className="details-row">
            <span className="details-label">Name : </span>
            <span className="details-value">{adminVerificationResult?.verifierName}</span>
          </div>
          <div className="details-row">
            <span className="details-label">Email : </span>
            <span className="details-value">{adminVerificationResult?.verifierEmail}</span>
          </div>
        </div>
      </div>

      {/* <hr className="hr-line" /> */}
<div className="severity-legend-section">
  <h2 className="legend-title">Severity Legend</h2>
  <div className="legend-grid">
    {severityLegend.map((item, idx) => (
      <div key={idx} className="client-legend-item-report" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
        {/* Color Box */}
        <div 
          className="legend-color-box-report" 
          style={{ backgroundColor: item.color, width: '20px', height: '20px', borderRadius: '3px', flexShrink: 0 }} 
        ></div>

        {/* Content */}
        <div className="legend-content-report">
          <p className="legend-type-report" style={{ margin: 0, fontWeight: 'bold' }}>{item.type}</p>
          <p className="legend-description-report" style={{ margin: '2px 0 4px 0', fontSize: '13px', color: '#555' }}>{item.description}</p>

          {/* Stars */}
          <div className="legend-stars-report">
            {Array.from({ length: 5 }).map((_, i) => (
              <span 
                key={i} 
                className={`star ${i < getStarsForStatus(item.type) ? 'filled' : ''}`} 
                style={{ color: i < getStarsForStatus(item.type) ? '#FFD700' : '#CCC', fontSize: '14px', marginRight: '2px' }}
              >
                ★
              </span>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
</div>


      <div className="report-header">
  <h1 className="final-report-title">FINAL BACKGROUND REPORT</h1>
</div>

      {/* TABLE OF CONTENTS */}
<div className="report-section table-of-contents">
  <h2 className="report-main-title">Executive Synopsis</h2>
  <div className="report-table-container">
    <table className="report-table">
      <thead>
        <tr>
          <th>Section</th>
          <th>Reference Link</th>
        </tr>
      </thead>
     <tbody>
  {personalCheck && (
    <tr>
      <td>Personal Report</td>
      <td>
        <button className="toc-link" onClick={() => scrollToSection('personal-report')}>
          Status - (REFER ANNEXURE)
        </button>
      </td>
    </tr>
  )}
  {addressCheck && (
    <tr>
      <td>Address Report</td>
      <td>
        <button className="toc-link" onClick={() => scrollToSection('address-report')}>
           Status - (REFER ANNEXURE)
        </button>
      </td>
    </tr>
  )}
    {educationCheck && (
    <tr>
      <td>Education Report</td>
      <td>
        <button className="toc-link" onClick={() => scrollToSection('education-report')}>
           Status - (REFER ANNEXURE)
        </button>
      </td>
    </tr>
  )}

  {employmentCheck && (
    <tr>
      <td>Employment Report</td>
      <td>
        <button className="toc-link" onClick={() => scrollToSection('employment-report')}>
           Status - (REFER ANNEXURE)
        </button>
      </td>
    </tr>
  )}

  {criminalCheckRequests?.length > 0 && (
    <tr>
      <td>Criminal Report</td>
      <td>
        <button className="toc-link" onClick={() => scrollToSection('criminal-report')}>
           Status - (REFER ANNEXURE)
        </button>
      </td>
    </tr>
  )}
  {employmentUANCheckRequests?.length > 0 && (
    <tr>
      <td>UAN / EPF Report</td>
      <td>
        <button className="toc-link" onClick={() => scrollToSection('uan-report')}>
          Status - (REFER ANNEXURE)
        </button>
      </td>
    </tr>
  )}
  
</tbody>

    </table>
  </div>
</div>


{/* PERSONAL REPORT */}
<div className="report-section" id="personal-report">
  <h2 className="report-main-title">PERSONAL REPORT</h2>
  
  <div className="report-table-container">
    <table className="report-table">
      <thead>
        <tr>
          <th>Label</th>
          <th>Stated / Verified</th>
        </tr>
      </thead>
      <tbody>
        {personalCheck ? (
          <>
            <tr><td>First Name</td><td>{personalCheck.firstName || "N/A"}</td></tr>
            <tr><td>Last Name</td><td>{personalCheck.lastName || "N/A"}</td></tr>
            <tr><td>Date of Birth</td><td>{personalCheck.dob || "N/A"}</td></tr>
            <tr><td>Father Name</td><td>{personalCheck.fatherName || "N/A"}</td></tr>
            <tr><td>Email</td><td>{personalCheck.email || "N/A"}</td></tr>
            <tr><td>Mobile</td><td>{personalCheck.mobileNumber || "N/A"}</td></tr>
            <tr><td>Remarks</td><td>{personalCheck.remarks || "N/A"}</td></tr>
            <tr>
              <td>Verification Status</td>
              <td>{renderStatusCell(personalCheck.verificationStatus ? "Verified" : "Not Verified")}</td>
            </tr>
          </>
        ) : (
          <tr>
            <td colSpan={2} style={{ textAlign: "center", fontStyle: "italic", color: "#777" }}>
              No records found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</div>


  

{/* ADDRESS REPORT */}
{addressCheck && (
  <>
    {/* <hr className="hr-line" /> */}
      <div className="report-section" id="address-report">

    <h2 className="report-main-title">ADDRESS REPORT</h2>
    <div className="report-table-container">   
      <table className="report-table">
        <thead><tr><th>Label</th><th>Details</th></tr></thead>
        <tbody>
          <tr>
            <td>Current Address</td>
            <td>{`${addressCheck.currentAddressLine1 || ''}, ${addressCheck.currentAddressLine2 || ''}, ${addressCheck.currentCity || ''}, ${addressCheck.currentState || ''}, ${addressCheck.currentPinCode || ''}`}</td>
          </tr>
          <tr>
            <td>Permanent Address</td>
            <td>{`${addressCheck.permanentAddressLine1 || ''}, ${addressCheck.permanentAddressLine2 || ''}, ${addressCheck.permanentCity || ''}, ${addressCheck.permanentState || ''}, ${addressCheck.permanentPinCode || ''}`}</td>
          </tr>
          <tr><td>Remarks</td><td>{addressCheck.remarks}</td></tr>
          <tr>
            <td>Verification Status</td>
            <td>{renderStatusCell(addressCheck.verificationStatus ? "Verified" : "Not Verified")}</td>
          </tr>
        </tbody>
      </table>
    </div>   
    </div>
  </>
)}


{/* Education REPORT */}
{educationCheck && (
  <>
    <div className="report-section" id="education-report">

    <h2 className="report-main-title">Education REPORT</h2>
    <div className="report-table-container">  
      <table className="report-table">
        <thead>
          <tr>
            <th>Qualification</th>
            <th>Institue</th>
            <th>Enrolled Year</th>
            <th>Passing Year</th>
            <th>Remarks</th>
             <th>Verification Status</th>
          </tr>
        </thead>
        <tbody>
          {educationCheck.map((edu, index) => (
            <tr key={index}>
              <td>{edu.educationLevel}</td>
              <td>{edu.instituteName}</td>
              <td>{edu.enrolledYear}</td>
              <td>{edu.passingYear}</td>
            <td>{edu.remarks}</td>
             <td>{renderStatusCell(edu.educationFileUrl ? "Verified" : "Not Verified")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>   
  
  </>
)}

{/* EMPLOYMENT REPORT */}
{employmentCheck?.length > 0 && (
  <div className="report-section" id="employment-report">
    <h2 className="report-main-title">EMPLOYMENT REPORT</h2>
    <div className="report-table-container">  
      <table className="report-table">
        <thead>
          <tr>
            <th>Employee Code</th>
            <th>Company Name</th>
            <th>Job Title</th>
            <th>Location</th>
            <th>HR Contact</th>
            <th>Working From</th>
            <th>Working To</th>
            <th>Remarks</th>
            <th>Verification Status</th>
          </tr>
        </thead>
        <tbody>
          {employmentCheck.map((emp, index) => (
            <tr key={index}>
              <td>{emp.employeeCode || "N/A"}</td>
              <td>{emp.companyName || "N/A"}</td>
              <td>{emp.jobTitle || "N/A"}</td>
              <td>{emp.location || "N/A"}</td>
              <td>
                {emp.hrContactEmail || "N/A"} <br />
                {emp.hrContactNumber || ""}
              </td>
              <td>{emp.workingFrom || "N/A"}</td>
              <td>{emp.workingTo || "N/A"}</td>
              <td>{emp.remarks || "N/A"}</td>
               <td>{renderStatusCell(emp.individualStatus ? "Verified" : "Not Verified")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

{/* ATTACHMENTS REPORT */}
{attachmentResponses?.length > 0 && (
  <div className="report-section" id="attachments-report">
    <h2 className="report-main-title">EMPLOYMENT REPORT</h2>

    <div className="report-table-container">
      <table className="report-table">
        <thead>
          <tr>
            <th>Company Name</th>
            <th>Employee Code</th>
            <th>Remarks</th>
            <th>Verification Status</th>
          </tr>
        </thead>
        <tbody>
          {attachmentResponses.map((att, idx) => (
            <tr key={idx}>
              <td>{att.companyName || "N/A"}</td>
              <td>{att.employeeCode || "N/A"}</td>
              <td>{att.remarks || "N/A"}</td>
              <td>{renderStatusCell(att.isVerified ? "Verified" : "Not Verified")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="attachments-preview-container">
      {attachmentResponses.map((att, idx) => {
        const fileUrl = att.fileUrl?.startsWith("http")
          ? att.fileUrl
          : att.fileUrl
          ? `https://bgccheckdev.s3.amazonaws.com/${att.fileUrl}`
          : null;

        if (!fileUrl) return null;

        return (
          <div key={idx} className="attachment-preview-item">
            <p>
              <strong>{att.companyName || "N/A"} - {att.employeeCode || "N/A"}</strong>
            </p>

            {/\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl) && (
              <img src={fileUrl} alt="Attachment Preview" className="attachment-preview" crossOrigin="anonymous" />
            )}

            {/\.(pdf)$/i.test(fileUrl) && (
              <iframe
                src={fileUrl}
                title={`PDF Preview ${idx}`}
                className="attachment-preview-pdf"
              />
            )}

            {!/\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(fileUrl) && (
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                Download
              </a>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}


{/* CRIMINAL REPORT */}
<div className="report-section" id="criminal-report">
  <h2 className="report-main-title">CRIMINAL REPORT</h2>
  <div className="report-table-container">
    <table className="report-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Petitioner</th>
          <th>Respondent</th>
          <th>State</th>
          <th>Case Type</th>
          <th>Case Name</th>
          <th>Case Number</th>
          <th>Filing Number</th>
          <th>Case Status</th>
          <th>FIR Link</th>
        </tr>
      </thead>
      <tbody>
        {criminalCheckRequests && criminalCheckRequests.length > 0 ? (
          criminalCheckRequests.map((criminal, index) => (
            <tr key={index}>
              <td>{criminal.year || "N/A"}</td>
              <td>{criminal.petitioner || "N/A"}</td>
              <td>{criminal.respondent || "N/A"}</td>
              <td>{criminal.state || "N/A"}</td>
              <td>{criminal.caseType || "N/A"}</td>
              <td>{criminal.caseName || "N/A"}</td>
              <td>{criminal.caseNumber || "N/A"}</td>
              <td>{criminal.filingNumber || "N/A"}</td>
              <td>{criminal.caseStatus || "N/A"}</td>
              <td>
                {criminal.firLink ? (
                  <a href={criminal.firLink} target="_blank" rel="noopener noreferrer">View FIR</a>
                ) : "N/A"}
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={10} style={{ textAlign: "center", fontStyle: "italic", color: "#777" }}>
              No records found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</div>



{/* UAN / EPF REPORT */}
{employmentUANCheckRequests?.length > 0 && (
  <>
    {/* <hr className="hr-line" /> */}
      <div className="report-section" id="uan-report">

    <h2 className="report-main-title">UAN / EPF REPORT</h2>
   <div className="report-table-container">
  <table className="report-table">
    <thead>
      <tr>
        <th>UAN</th>
        <th>Name</th>
        <th>Member ID</th>
        <th>DOJ EPF</th>
        <th>DOE EPF</th>
        <th>Company Name</th>
        <th>Month History</th>
        <th>Verified</th>
      </tr>
    </thead>
    <tbody>
      {employmentUANCheckRequests.map((uan, index) => (
        <tr key={index}>
          <td>{uan.uan}</td>
          <td>{uan.name}</td>
          <td>{uan.memberId}</td>
          <td>{uan.dojEpf}</td>
          <td>{uan.doeEpf}</td>
          <td>{uan.companyName}</td>
          <td>{uan.monthHistory}</td>
          <td>{renderStatusCell(uan.verified ? "Verified" : "Not Verified")}</td>
        </tr>
      ))}
    </tbody>
  </table>
  </div>
</div>
  </>
)}


 
{/* 
{attachmentResponses?.length > 0 ? (
  <div className="report-section" id="attachments-report">
    <h2 className="report-main-title">ATTACHMENTS REPORT</h2>

    <div className="report-table-container">
      <table className="report-table">
        <thead>
          <tr>
            <th>Company Name</th>
            <th>Employee Code</th>
            <th>Verification Status</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {attachmentResponses.map((att, idx) => (
            <tr key={idx}>
              <td>{att.companyName || "N/A"}</td>
              <td>{att.employeeCode || "N/A"}</td>
              <td>{renderStatusCell(att.isVerified ? "Verified" : "Not Verified")}</td>
              <td>{att.remarks || "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    { <div className="attachments-preview-container">
      {attachmentResponses.map((att, idx) => {
        const fileUrl = att.fileUrl?.startsWith("http")
          ? att.fileUrl
          : att.fileUrl
          ? `https://bgccheckdev.s3.amazonaws.com/${att.fileUrl}`
          : null;

        if (!fileUrl) return null;

        return (
          <div key={idx} className="attachment-preview-item">
            <p>
              <strong>{att.companyName || "N/A"} - {att.employeeCode || "N/A"}</strong>
            </p>

            {/\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl) && (
              <img src={fileUrl} alt="Attachment Preview" className="attachment-preview" crossOrigin="anonymous" />
            )}

            {/\.(pdf)$/i.test(fileUrl) && (
              <iframe
                src={fileUrl}
                title={`PDF Preview ${idx}`}
                className="attachment-preview-pdf"
              />
            )}

            {!/\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(fileUrl) && (
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                Download
              </a>
            )}
          </div>
        );
      })}
    </div> }
  </div>
) : (
  <div className="report-section" id="attachments-report">
    <h2 className="report-main-title">ATTACHMENTS REPORT</h2>
    <p>No Records Found</p>
  </div>
)} */}



 
    
    </div>
    
    </div>
  );
};

export default FinalReportPage;
